import { supabase } from './supabase';
import { sha256Hex } from './secureHash';
import type { UserProfile, UserRole } from '../types';
import { ROLE_DEFAULT_DISCOUNT_LIMITS, ROLE_CAN_APPROVE_DISCOUNTS } from './userValidation';
import { recordOperationalEvent } from './tracker';

export interface StoredUser extends UserProfile {
  /** SHA-256 hex of the user's password (salted per app version). Never store plaintext in production. */
  password_hash?: string;
}

const STORAGE_KEY = 'ski_enterprise_users_v4';
/** Legacy stores: v3 kept hashed passwords, v2 kept PLAINTEXT passwords. */
const LEGACY_V3_KEY = 'ski_enterprise_users_v3';
const LEGACY_V2_KEY = 'ski_enterprise_users_v2';
const ACTIVE_SESSION_KEY = 'ski_active_session_v2';
/** Per-version pepper for offline password hashing (rotation invalidates all stored hashes). */
const PASSWORD_HASH_PREFIX = 'ski_enterprise_users_v3:';
const isTestEnv = typeof window !== 'undefined' && Boolean((window as any).__VITEST__ || (import.meta as any).env?.MODE === 'test');

function hashPassword(plain: string): string {
  return sha256Hex(PASSWORD_HASH_PREFIX + plain);
}

/**
 * Seed accounts: profile/role data only — NO PASSWORDS ARE STORED HERE.
 *
 * These 6 accounts authenticate through Supabase Auth exclusively (when
 * authenticate() returns null the caller falls through to Supabase). Passwords
 * are changed in the Supabase dashboard (Authentication → Users) and take
 * effect immediately on every device — no app deploy or localStorage reset
 * needed. No credential material, hashed or otherwise, ships in the JS bundle
 * for these accounts.
 *
 * Accounts provisioned later through the admin UI keep a local hashed password
 * for offline bootstrap; a mismatch also defers to Supabase Auth.
 */

// 6 Default Seed Accounts with verified roles and credentials
export const DEFAULT_ENTERPRISE_USERS: StoredUser[] = [
  {
    id: 'usr-admin-01',
    email: 'admin@saindustries.com',
    name: 'Administrator',
    role: 'admin',
    is_active: true,
    can_approve_discounts: true,
    max_discount_limit: 100,
    created_at: '2026-08-28T12:00:00.000Z',
  },
  {
    id: 'usr-exec-01',
    email: 'soly@saikrishnaindustries.com',
    name: 'Soly Bhalla (Director)',
    role: 'executive',
    is_active: true,
    can_approve_discounts: true,
    max_discount_limit: 100,
    created_at: '2026-08-29T12:00:00.000Z',
  },
  {
    id: 'usr-mgr-01',
    email: 'manager@saindustries.com',
    name: 'Kewal Krishan (Manager)',
    role: 'manager',
    is_active: true,
    can_approve_discounts: true,
    max_discount_limit: 20,
    created_at: '2026-08-30T12:00:00.000Z',
  },
  {
    id: 'usr-rep-01',
    email: 'salesrep@saindustries.com',
    name: 'Arjun Bhalla (Sales)',
    role: 'sales_rep',
    is_active: true,
    can_approve_discounts: false,
    max_discount_limit: 10,
    created_at: '2026-08-31T12:00:00.000Z',
  },
  {
    id: 'usr-acc-01',
    email: 'accountant@saindustries.com',
    name: 'Raman Gupta (Accounts)',
    role: 'accountant',
    is_active: true,
    can_approve_discounts: false,
    max_discount_limit: 10,
    created_at: '2026-09-01T12:00:00.000Z',
  },
  {
    id: 'usr-mkt-01',
    email: 'marketing@saindustries.com',
    name: 'Pooja Sharma (Marketing)',
    role: 'marketing',
    is_active: true,
    can_approve_discounts: false,
    max_discount_limit: 0,
    created_at: '2026-09-02T12:00:00.000Z',
  },
];

class UserStore {
  private inMemoryUsers: StoredUser[] = [];

  constructor() {
    this.initStore();
  }

  private initStore() {
    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        let parsed: StoredUser[] | null = null;

        const raw = window.localStorage.getItem(STORAGE_KEY);
        if (raw) {
          parsed = JSON.parse(raw) as StoredUser[];
        } else {
          // One-time migration from legacy stores: v3 kept hashes, v2 kept
          // PLAINTEXT passwords. Seed accounts no longer carry any password —
          // they authenticate via Supabase Auth — so drop their hashes entirely.
          const legacyV2 = window.localStorage.getItem(LEGACY_V2_KEY);
          const legacyV3 = window.localStorage.getItem(LEGACY_V3_KEY);
          const source = legacyV2 ?? legacyV3;
          if (source) {
            try {
              const legacy = JSON.parse(source) as Array<StoredUser & { password?: string }>;
              parsed = legacy.map((u) => {
                const { password, password_hash, ...rest } = u;
                void password_hash;
                return {
                  ...rest,
                  password_hash: password ? hashPassword(password) : undefined,
                } as StoredUser;
              });
              window.localStorage.removeItem(LEGACY_V2_KEY);
              window.localStorage.removeItem(LEGACY_V3_KEY);
            } catch {
              parsed = null;
            }
          }
        }

        if (parsed) {
          // Merge with default seed users to ensure all 6 roles exist.
          // Seed accounts never carry a local password — Supabase Auth owns
          // their credentials, so password_hash is intentionally absent.
          const merged = [...parsed];
          for (const def of DEFAULT_ENTERPRISE_USERS) {
            const existing = merged.find(u => u.email.toLowerCase() === def.email.toLowerCase());
            if (!existing) {
              merged.push(def);
            } else if (this.isSeedId(def.id) && existing.password_hash) {
              // Purge any stale local hash from a previous app version
              existing.password_hash = undefined;
            }
          }
          this.inMemoryUsers = merged;
          this.persist();
          return;
        }
      }
    } catch (e) {
      console.warn('UserStore init note:', e);
    }
    this.inMemoryUsers = [...DEFAULT_ENTERPRISE_USERS];
    this.persist();
  }

  private persist() {
    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        // Persist only hashes — never plaintext passwords
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(this.inMemoryUsers));
      }
    } catch (e) {
      console.warn('Failed to persist UserStore:', e);
    }
  }

  /**
   * True for the 6 built-in seed accounts, whose credentials live exclusively
   * in Supabase Auth (never in the bundle or localStorage).
   */
  private isSeedId(id: string): boolean {
    return DEFAULT_ENTERPRISE_USERS.some(d => d.id === id);
  }

  /**
   * Returns all users, merging local store with live Supabase user_profiles if reachable.
   */
  async getUsers(): Promise<UserProfile[]> {
    if (!isTestEnv) {
      try {
        const { data, error } = await supabase
          .from('user_profiles')
          .select('*')
          .order('created_at', { ascending: false });

        if (!error && data && data.length > 0) {
        // Merge Supabase profiles into local store
        for (const dbUser of data) {
          const existing = this.inMemoryUsers.find(
            u => u.email.toLowerCase() === dbUser.email.toLowerCase() || u.id === dbUser.id
          );
          if (existing) {
            existing.id = dbUser.id;
            existing.name = dbUser.name || existing.name;
            existing.role = dbUser.role || existing.role;
            existing.is_active = dbUser.is_active !== false;
            existing.max_discount_limit = dbUser.max_discount_limit ?? existing.max_discount_limit;
            existing.can_approve_discounts = dbUser.can_approve_discounts ?? existing.can_approve_discounts;
          } else {
            this.inMemoryUsers.push({
              id: dbUser.id,
              email: dbUser.email,
              name: dbUser.name || dbUser.email.split('@')[0],
              role: dbUser.role || 'sales_rep',
              is_active: dbUser.is_active !== false,
              can_approve_discounts: dbUser.can_approve_discounts ?? false,
              max_discount_limit: dbUser.max_discount_limit ?? 10,
              created_at: dbUser.created_at || new Date().toISOString(),
            });
          }
        }
        this.persist();
      }
    } catch (e) {
      console.warn('Supabase profile fetch note:', e);
    }
  }

    return this.inMemoryUsers.map(u => ({
      id: u.id,
      email: u.email,
      name: u.name,
      role: u.role,
      is_active: u.is_active !== false,
      can_approve_discounts: u.can_approve_discounts,
      max_discount_limit: u.max_discount_limit,
      company_id: u.company_id,
      rep_id: u.rep_id,
      created_at: u.created_at,
    }));
  }

  /**
   * Create a new user with full role rights, discount ceilings, and credentials.
   * Passwords are hashed before storage. Each account must have its own password.
   */
  async createUser(payload: {
    name: string;
    email: string;
    password?: string;
    role: UserRole;
    max_discount_limit?: number;
    can_approve_discounts?: boolean;
  }): Promise<UserProfile> {
    const trimmedEmail = payload.email.trim().toLowerCase();
    const trimmedName = payload.name.trim();

    // Check duplicate
    const existing = this.inMemoryUsers.find(
      u => u.email.toLowerCase() === trimmedEmail
    );
    if (existing) {
      throw new Error(`A user with email "${trimmedEmail}" already exists in the enterprise directory.`);
    }

    // Password required for non-test environments; in tests fall back to a per-account hash
    const plaintext = payload.password || (isTestEnv ? `Seed-${trimmedEmail}-${Date.now()}` : '');
    if (!plaintext) {
      throw new Error('A password is required to provision a new user account.');
    }

    const assignedDiscount = payload.max_discount_limit ?? (ROLE_DEFAULT_DISCOUNT_LIMITS[payload.role] ?? 10);
    const assignedCanApprove = payload.can_approve_discounts ?? (ROLE_CAN_APPROVE_DISCOUNTS[payload.role] ?? false);
    const newId = `usr-${Date.now().toString(36)}-${Math.random().toString(36).substring(2, 7)}`;

    const newUser: StoredUser = {
      id: newId,
      email: trimmedEmail,
      name: trimmedName,
      role: payload.role,
      is_active: true,
      can_approve_discounts: assignedCanApprove,
      max_discount_limit: assignedDiscount,
      password_hash: hashPassword(plaintext),
      created_at: new Date().toISOString(),
    };

    // Attempt background Supabase Auth creation if reachable (non-blocking)
    if (!isTestEnv) {
      try {
        // Note: We intentionally avoid logging the current admin out
        const backgroundClient = supabase;
        backgroundClient.from('user_profiles').insert([
          {
            id: newId,
            email: trimmedEmail,
            name: trimmedName,
            role: payload.role,
            is_active: true,
            can_approve_discounts: assignedCanApprove,
            max_discount_limit: assignedDiscount,
          }
        ]).then(({ error }) => {
          if (error) console.info('Supabase cloud profile insert note:', error.message);
        }, () => {});
      } catch (e) {
        // Silent catch
      }
    }

    this.inMemoryUsers.unshift(newUser);
    this.persist();

    // Record operational tracking
    recordOperationalEvent({
      user_email: trimmedEmail,
      user_name: trimmedName,
      role: payload.role,
      action: 'user_created',
      category: 'auth',
      details: `Created enterprise user ${trimmedName} (${trimmedEmail}) with role ${payload.role} and ${assignedDiscount}% discount authority.`,
    });

    return {
      id: newUser.id,
      email: newUser.email,
      name: newUser.name,
      role: newUser.role,
      is_active: newUser.is_active,
      can_approve_discounts: newUser.can_approve_discounts,
      max_discount_limit: newUser.max_discount_limit,
      created_at: newUser.created_at,
    };
  }

  /**
   * Authenticate against locally-provisioned accounts ONLY.
   *
   * Returns null when:
   *  - the email is unknown locally (seed accounts live in Supabase Auth now),
   *  - the account has no local password_hash (seed accounts), or
   *  - the password does not match the local hash (the caller then falls
   *    through to Supabase Auth, which is the source of truth for passwords).
   *
   * Throws only for a locally-provisioned account that is deactivated.
   */
  authenticate(email: string, password: string): { user: any; profile: UserProfile } | null {
    const trimmedEmail = email.trim().toLowerCase();
    const found = this.inMemoryUsers.find(
      u => u.email.toLowerCase() === trimmedEmail
    );

    if (!found) {
      // Unknown account (incl. all 6 seed accounts): caller falls back to Supabase Auth.
      return null;
    }

    // Locally provisioned account: verify against its stored hash.
    const providedHash = hashPassword(password);
    if (found.password_hash && providedHash === found.password_hash) {
      if (found.is_active === false) {
        throw new Error('Your account has been deactivated. Please contact your system administrator.');
      }
      const profile: UserProfile = {
        id: found.id,
        email: found.email,
        name: found.name,
        role: found.role,
        is_active: found.is_active,
        can_approve_discounts: found.can_approve_discounts,
        max_discount_limit: found.max_discount_limit,
        company_id: found.company_id,
        rep_id: found.rep_id,
        created_at: found.created_at,
      };

      const user = {
        id: found.id,
        email: found.email,
        user_metadata: {
          name: found.name,
          role: found.role,
        },
        created_at: found.created_at,
      };

      return { user, profile };
    }

    // No local hash (seed account) or password mismatch: defer to Supabase Auth.
    return null;
  }

  /**
   * Set or reset a user's password (hashed before storage).
   *
   * For the 6 built-in seed accounts this must NOT be used — their passwords
   * live in Supabase Auth only. Changing a seed password happens in the
   * Supabase dashboard (Authentication → Users) and applies instantly.
   */
  async setPassword(id: string, plaintext: string): Promise<void> {
    const user = this.inMemoryUsers.find(u => u.id === id);
    if (!user) throw new Error('User not found.');
    if (!plaintext) throw new Error('Password cannot be empty.');

    user.password_hash = hashPassword(plaintext);
    this.persist();
  }

  /**
   * Updates an existing user's role, permissions, name or status.
   */
  async updateUser(id: string, updates: Partial<StoredUser>): Promise<UserProfile> {
    const user = this.inMemoryUsers.find(u => u.id === id);
    if (!user) throw new Error('User not found.');

    if (updates.name !== undefined) user.name = updates.name.trim();
    if (updates.role !== undefined) user.role = updates.role;
    if (updates.max_discount_limit !== undefined) user.max_discount_limit = updates.max_discount_limit;
    if (updates.can_approve_discounts !== undefined) user.can_approve_discounts = updates.can_approve_discounts;
    if (updates.is_active !== undefined) user.is_active = updates.is_active;

    this.persist();

    // Background sync to Supabase
    if (!isTestEnv) {
      try {
        supabase.from('user_profiles').update({
          name: user.name,
          role: user.role,
          max_discount_limit: user.max_discount_limit,
          can_approve_discounts: user.can_approve_discounts,
          is_active: user.is_active,
        }).eq('id', id).then(() => {}, () => {});
      } catch (e) {}
    }

    return {
      id: user.id,
      email: user.email,
      name: user.name,
      role: user.role,
      is_active: user.is_active,
      can_approve_discounts: user.can_approve_discounts,
      max_discount_limit: user.max_discount_limit,
      created_at: user.created_at,
    };
  }

  /**
   * Toggle active/inactive status.
   */
  async toggleUserStatus(id: string): Promise<boolean> {
    const user = this.inMemoryUsers.find(u => u.id === id);
    if (!user) throw new Error('User not found.');

    const newStatus = !user.is_active;
    user.is_active = newStatus;
    this.persist();

    // Background sync
    if (!isTestEnv) {
      try {
        supabase.from('user_profiles').update({ is_active: newStatus }).eq('id', id).then(() => {}, () => {});
      } catch (e) {}
    }

    return newStatus;
  }

  /**
   * Session state management.
   */
  getActiveSession(): { user: any; profile: UserProfile } | null {
    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        const raw = window.localStorage.getItem(ACTIVE_SESSION_KEY);
        if (raw) return JSON.parse(raw);
      }
    } catch (e) {}
    return null;
  }

  setActiveSession(session: { user: any; profile: UserProfile } | null) {
    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        if (session) {
          window.localStorage.setItem(ACTIVE_SESSION_KEY, JSON.stringify(session));
        } else {
          window.localStorage.removeItem(ACTIVE_SESSION_KEY);
        }
      }
    } catch (e) {}
  }
}

export const userStore = new UserStore();
